import { ref, computed, onBeforeUnmount } from "vue";
import { isAxiosError } from "axios";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

export type CompanyStatus =
  "pending" | "in_progress" | "completed" | "skipped" | "error";

export interface CompanyProgress {
  companyId: string;
  status: CompanyStatus;
  message?: string | null;
  issuedCount?: number | null;
}

interface BackendProgress {
  current: number;
  total: number;
  current_company_id: string;
  results: Record<
    string,
    {
      status: CompanyStatus;
      message: string | null;
      issued_count: number | null;
      skipped_count: number | null;
    }
  >;
  cancelled: boolean;
}

interface BackendResult {
  results: Record<
    string,
    {
      status: CompanyStatus;
      message: string | null;
      issued_count: number | null;
      skipped_count: number | null;
    }
  >;
  total: number;
  completed: number;
  skipped: number;
  errors: number;
  cancelled: boolean;
}

export type ModalState = "initial" | "processing" | "completed" | "cancelled";

const POLLING_INTERVAL_MS = 2000;
const AUTH_ERROR_CODES = new Set([401, 403]);

export function useBulkIssuance() {
  const { globalErrorHandler } = useGlobalHandlers();
  const modalState = ref<ModalState>("initial");
  const companies = ref<string[]>([]);
  const selectedCompanies = ref<Set<string>>(new Set());
  const periodFrom = ref("");
  const periodTo = ref("");
  const taskId = ref<string | null>(null);
  const progress = ref<BackendProgress | null>(null);
  const finalResult = ref<BackendResult | null>(null);
  const errorMessage = ref<string | null>(null);
  const pollingInterval = ref<number | null>(null);

  const companyStatuses = computed<CompanyProgress[]>(() => {
    if (!companies.value.length) return [];

    const currentIdx = progress.value?.current ?? -1;
    const results = finalResult.value?.results ?? progress.value?.results ?? {};

    return companies.value.map((companyId, idx) => {
      if (results[companyId]) {
        return {
          companyId,
          status: results[companyId].status,
          message: results[companyId].message,
          issuedCount: results[companyId].issued_count,
        };
      }
      if (
        idx === currentIdx &&
        progress.value?.current_company_id === companyId
      ) {
        return { companyId, status: "in_progress" as const };
      }
      return { companyId, status: "pending" as const };
    });
  });

  const processedCompanies = computed(() =>
    companyStatuses.value
      .filter((c) => c.status === "completed" || c.status === "skipped")
      .map((c) => c.companyId)
  );

  const isConfirmDisabled = computed(
    () =>
      selectedCompanies.value.size === 0 ||
      (!!periodFrom.value &&
        !!periodTo.value &&
        periodFrom.value > periodTo.value)
  );

  const toggleCompany = (companyId: string) => {
    const next = new Set(selectedCompanies.value);
    if (next.has(companyId)) {
      next.delete(companyId);
    } else {
      next.add(companyId);
    }
    selectedCompanies.value = next;
  };

  const toggleAllCompanies = (selected: boolean) => {
    selectedCompanies.value = selected ? new Set(companies.value) : new Set();
  };

  const stopPolling = () => {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value);
      pollingInterval.value = null;
    }
  };

  const setErrorState = (message: string) => {
    stopPolling();
    modalState.value = "completed";
    errorMessage.value = message;
  };

  const startPolling = () => {
    pollingInterval.value = window.setInterval(async () => {
      if (!taskId.value) return;

      try {
        const response = await goSolidApi.get(
          `/api/async-tasks/${taskId.value}/status`
        );

        if (response.data.progress) {
          progress.value = response.data.progress;
        }

        if (response.data.ready) {
          stopPolling();

          if (response.data.result) {
            finalResult.value = response.data.result;

            if (response.data.result.cancelled) {
              modalState.value = "cancelled";
            } else {
              modalState.value = "completed";
            }
          } else if (response.data.status === "FAILURE") {
            modalState.value = "completed";
            errorMessage.value = response.data.error ?? "Task failed";
          } else {
            modalState.value = "completed";
          }
        }
      } catch (error: unknown) {
        if (
          isAxiosError(error) &&
          error.response &&
          AUTH_ERROR_CODES.has(error.response.status)
        ) {
          setErrorState("Access denied.");
          return;
        }
      }
    }, POLLING_INTERVAL_MS);
  };

  const fetchCompanies = async () => {
    try {
      const response = await goSolidApi.get("/api/registry/hknr/companies");
      companies.value = response.data.companies;
      selectedCompanies.value = new Set(response.data.companies);
    } catch {
      errorMessage.value = "Failed to fetch companies";
    }
  };

  const startIssuance = async () => {
    modalState.value = "processing";
    errorMessage.value = null;
    progress.value = null;
    finalResult.value = null;

    try {
      const body: Record<string, unknown> = {
        registry_company_ids: [...selectedCompanies.value],
      };
      if (periodFrom.value) body.production_month_from = periodFrom.value;
      if (periodTo.value) body.production_month_to = periodTo.value;

      const response = await goSolidApi.post(
        "/api/registry/hknr/bulk-issuance",
        body
      );
      taskId.value = response.data.task_id;
      companies.value = response.data.companies;
      startPolling();
    } catch (error: unknown) {
      modalState.value = "initial";
      globalErrorHandler(error);
    }
  };

  const cancelIssuance = async () => {
    if (!taskId.value) return;

    try {
      await goSolidApi.post(`/api/async-tasks/${taskId.value}/cancel`);
    } catch (error: unknown) {
      globalErrorHandler(error);
    }
  };

  const reset = () => {
    stopPolling();
    modalState.value = "initial";
    taskId.value = null;
    progress.value = null;
    finalResult.value = null;
    errorMessage.value = null;
    periodFrom.value = "";
    periodTo.value = "";
    selectedCompanies.value = new Set();
  };

  onBeforeUnmount(() => stopPolling());

  return {
    modalState,
    companies,
    selectedCompanies,
    periodFrom,
    periodTo,
    companyStatuses,
    processedCompanies,
    isConfirmDisabled,
    errorMessage,
    fetchCompanies,
    startIssuance,
    cancelIssuance,
    toggleCompany,
    toggleAllCompanies,
    reset,
  };
}
